import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Loader2, FileDown, Printer, Package, ExternalLink, Pencil, Trash2 } from "lucide-react";
import api, { fmtIDR, fmtDate, printPdf } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import PoDetailModal, { PO_STATUS } from "@/components/app/PoDetailModal";

const TONES = {
  green: "text-[#3f7a52] bg-[#E4F9EC]/70 border-[#B7E7C7] hover:bg-[#D8EEDF]",
  blue: "text-[#3B4A63] bg-[#E5EEFA]/70 border-[#B0C7EA] hover:bg-[#D3E1F5]",
  slate: "text-[#626061] bg-[#F4F5F7] border-[#DCE0E7] hover:bg-[#E9EBEF]",
  red: "text-[#CE6968] bg-[#FDE7E9]/70 border-[#FFB3BF] hover:bg-[#FBD6DA]",
};

function IconBtn({ title, testid, onClick, tone, children }) {
  return (
    <button
      title={title}
      data-testid={testid}
      onClick={onClick}
      className={`w-9 h-9 rounded-full border inline-flex items-center justify-center transition-all active:scale-95 ${TONES[tone]}`}
    >
      {children}
    </button>
  );
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function PurchaseOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = ["owner", "finance", "sales_director"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openPo, setOpenPo] = useState(null);
  const [poMode, setPoMode] = useState("view");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [po, v, p, q] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/clients", { params: { kind: "vendor" } }),
        api.get("/projects"),
        api.get("/quotations"),
      ]);
      setRows(po.data || []);
      setVendors(v.data || []);
      setProjects(p.data || []);
      setQuotations(q.data || []);
    } catch { toast.error("Gagal memuat Purchase Order"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const openId = params.get("open");
    if (!openId) return;
    setPoMode("view");
    api.get(`/purchase-orders/${openId}`).then((r) => setOpenPo(r.data)).catch(() => {});
    params.delete("open");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === "unpaid" ? !["issued", "partial"].includes(r.status) : statusFilter !== "all" && r.status !== statusFilter) return false;
      if (qs && !`${r.number} ${r.vendor_name} ${r.project_title || ""} ${r.project_name || ""}`.toLowerCase().includes(qs)) return false;
      return true;
    });
  }, [rows, q, statusFilter]);

  const stats = useMemo(() => {
    const scope = rows.filter((r) => r.status !== "void");
    const total = scope.reduce((a, r) => a + num(r.grand_total), 0);
    const paid = scope.reduce((a, r) => a + num(r.paid_amount), 0);
    const t = new Date().toISOString().slice(0, 10);
    return {
      total, paid,
      outstanding: total - paid,
      pct: total > 0 ? (paid * 100) / total : 0,
      draftCount: rows.filter((r) => r.status === "draft").length,
      overdue: scope.filter((r) => r.deadline && r.deadline < t && r.status !== "paid"),
      count: scope.length,
    };
  }, [rows]);

  const openNew = async () => {
    try {
      const { data } = await api.get("/purchase-orders/prefill");
      setPoMode("edit");
      setOpenPo({ ...data, id: null, status: "draft", number: "" });
    } catch { toast.error("Gagal menyiapkan PO baru"); }
  };

  const openView = (id) => { setPoMode("view"); api.get(`/purchase-orders/${id}`).then((res) => setOpenPo(res.data)); };
  const openEdit = (id) => { setPoMode("edit"); api.get(`/purchase-orders/${id}`).then((res) => setOpenPo(res.data)); };

  const remove = async (row) => {
    if (!window.confirm(`Hapus PO ${row.number}?`)) return;
    try { await api.delete(`/purchase-orders/${row.id}`); toast.success("PO dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Purchase Order</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Daftar Pesanan</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Pesanan ke vendor beserta pencatatan pembayarannya.</p>
        </div>
        {canManage && (
          <button
            onClick={openNew} data-testid="po-add-btn"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black transition-all shadow-[0_8px_20px_rgba(9,8,8,0.18)]"
          >
            <Plus className="w-4 h-4" /> Tambah PO
          </button>
        )}
      </div>

      {!loading && (
        <div className="bg-white rounded-[22px] p-5 md:p-6 card-soft" data-testid="po-summary">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-[#090808]">Ringkasan Purchase Order</h2>
            <span className="text-xs text-[#A0A3A8] font-semibold" data-testid="po-summary-count">Berdasarkan {stats.count} PO aktif</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="rounded-2xl p-4 bg-[#090808] text-white" data-testid="po-kpi-value">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#B7BAC2]">Nilai PO</div>
              <div className="text-xl md:text-2xl font-extrabold mt-2 font-num tabular-nums break-words">{fmtIDR(stats.total)}</div>
              <div className="text-[11px] text-[#8A8D96] mt-1.5 font-semibold">Tanpa yang dibatalkan</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="po-kpi-outstanding">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Belum Dibayar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808] break-words">{fmtIDR(stats.outstanding)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Sisa kewajiban ke vendor</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="po-kpi-paid">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Sudah Dibayar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#3EB876] break-words">{fmtIDR(stats.paid)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Pembayaran tercatat</div>
            </div>
            <div className={`rounded-2xl p-4 border ${stats.overdue.length ? "bg-[#FDE7E9] border-[#FFB3BF]" : "bg-[#FAFBFD] border-[#EEF0F4]"}`} data-testid="po-kpi-overdue">
              <div className={`text-[10px] uppercase tracking-[0.12em] font-extrabold ${stats.overdue.length ? "text-[#8E4747]" : "text-[#A0A3A8]"}`}>Lewat Deadline</div>
              <div className={`text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums break-words ${stats.overdue.length ? "text-[#CE6968]" : "text-[#090808]"}`}>
                {fmtIDR(stats.overdue.reduce((a, r) => a + num(r.outstanding), 0))}
              </div>
              <div className={`text-[11px] mt-1.5 font-semibold ${stats.overdue.length ? "text-[#8E4747]" : "text-[#A0A3A8]"}`}>
                {stats.overdue.length ? `Dari ${stats.overdue.length} PO lewat deadline` : "Semua tepat waktu"}
              </div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="po-kpi-draft">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Draft</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808]">{stats.draftCount}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Belum diterbitkan</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#F0F1F4] flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="text-xs text-[#626061] font-semibold whitespace-nowrap">
              Progres pembayaran: <b className="text-[#090808]">{fmtIDR(stats.paid)}</b> / {fmtIDR(stats.total)}
            </div>
            <div className="flex-1 min-w-[140px] h-2.5 bg-[#EEF0F4] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#2FA968] rounded-full transition-all" style={{ width: `${Math.min(100, stats.pct)}%` }} />
            </div>
            <div className="text-sm font-extrabold text-[#2FA968] tabular-nums" data-testid="po-progress-pct">{stats.pct.toFixed(1).replace(".", ",")}%</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="po-status-toggle">
        {[["all", "Semua", "bg-[#090808] text-white"], ["unpaid", "Belum Lunas", "bg-[#3B4A63] text-white"], ["paid", "Lunas", "bg-[#2FA968] text-white"], ["draft", "Draft", "bg-[#626061] text-white"]].map(([k, l, color]) => {
          const active = statusFilter === k;
          const n = k === "all" ? rows.length : k === "unpaid" ? rows.filter((r) => ["issued", "partial"].includes(r.status)).length : rows.filter((r) => r.status === k).length;
          return (
            <button
              key={k} onClick={() => setStatusFilter(k)} data-testid={`po-status-toggle-${k}`}
              className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-all ${active ? `${color} shadow-[0_4px_12px_rgba(0,0,0,0.10)]` : "bg-white text-[#626061] hover:bg-[#F4F5F7] card-soft"}`}
            >
              {l}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-search"
            placeholder="Cari nomor / vendor / proyek..."
            className="w-full pl-11 pr-4 h-12 rounded-full bg-white border border-transparent card-soft text-sm placeholder:text-[#A0A3A8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#090808]/40"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#DCE0E7] card-soft overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#A0A3A8]" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="po-empty">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-[#F4F5F7] flex items-center justify-center text-[#A0A3A8]"><Package className="w-5 h-5" /></div>
            <div className="text-sm font-bold text-[#090808] mt-3">Belum ada Purchase Order</div>
            <div className="text-xs text-[#A0A3A8] mt-1">Buat PO untuk mencatat pesanan ke vendor.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#626061] font-bold">
                  <th className="px-5 py-3">No. Purchase Order</th>
                  <th className="px-5 py-3">Pelanggan</th>
                  <th className="px-5 py-3">Tanggal</th>
                  <th className="px-5 py-3 text-right">Total Tagihan</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const st = PO_STATUS[r.status] || PO_STATUS.draft;
                  const overdue = r.status !== "paid" && r.status !== "void" && r.deadline && r.deadline < new Date().toISOString().slice(0, 10);
                  return (
                    <tr
                      key={r.id} data-testid={`po-row-${r.id}`}
                      onClick={() => openView(r.id)}
                      className="border-t border-[#F4F5F7] hover:bg-[#FAFBFD] cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-[13px] font-num font-bold text-[#090808] tabular-nums">{r.number}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mt-0.5">{r.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-bold text-[#090808]">{r.vendor_name}</div>
                        <div className="text-xs text-[#626061] truncate max-w-[240px]">{r.project_title || r.project_name || "—"}</div>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Terbit</span>
                          <span className="text-xs tabular-nums text-[#3B4A63] font-semibold">{r.date ? fmtDate(r.date) : "-"}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deadline</span>
                          <span className={`text-xs tabular-nums font-semibold ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`} data-testid={`po-deadline-${r.id}`}>
                            {r.deadline ? fmtDate(r.deadline) : "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="text-sm font-num font-bold text-[#090808] tabular-nums">{fmtIDR(r.grand_total)}</div>
                        <div className="text-[11px] text-[#A0A3A8] font-num tabular-nums">Dasar {fmtIDR(r.subtotal || 0)}</div>
                        {Number(r.paid_amount || 0) > 0 && (
                          <div className="text-[11px] font-num tabular-nums mt-0.5" data-testid={`po-paid-${r.id}`}>
                            <span className="text-[#3EB876] font-semibold">{fmtIDR(r.paid_amount)}</span>
                            <span className="text-[#A0A3A8]"> · sisa </span>
                            <span className="text-[#CE6968] font-semibold">{fmtIDR(Math.max(0, Number(r.grand_total || 0) - Number(r.paid_amount || 0)))}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center h-6 px-2.5 rounded-full border text-[10px] font-bold ${st.chip}`} data-testid={`po-status-${r.id}`}>{st.label}</span>
                        {overdue && <div className="text-[10px] font-bold text-[#CE6968] mt-1 uppercase tracking-widest">Lewat deadline</div>}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <IconBtn title="Unduh PDF" testid={`po-download-${r.id}`} onClick={() => api.get(`/purchase-orders/${r.id}/pdf`, { responseType: "blob" }).then((res) => { const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `PO-${r.number.replace(/\//g, "-")}.pdf`; a.click(); })} tone="green"><FileDown className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Print PDF" testid={`po-print-${r.id}`} onClick={() => printPdf(`/purchase-orders/${r.id}/pdf`).catch(() => toast.error("Gagal mencetak"))} tone="blue"><Printer className="w-4 h-4" /></IconBtn>
                          {r.project_id && (
                            <IconBtn title="Buka Proyek" testid={`po-project-${r.id}`} onClick={() => navigate(`/projects?open=${r.project_id}`)} tone="blue"><ExternalLink className="w-4 h-4" /></IconBtn>
                          )}
                          <IconBtn title="Edit" testid={`po-edit-${r.id}`} onClick={() => openEdit(r.id)} tone="slate"><Pencil className="w-4 h-4" /></IconBtn>
                          {canManage && r.status === "draft" && (
                            <IconBtn title="Hapus" testid={`po-delete-${r.id}`} onClick={() => remove(r)} tone="red"><Trash2 className="w-4 h-4" /></IconBtn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openPo && (
        <PoDetailModal
          po={openPo} vendors={vendors} projects={projects} quotations={quotations} canManage={canManage} initialMode={poMode}
          onClose={() => setOpenPo(null)} onSaved={(fresh) => { setOpenPo(fresh); load(); }} onDeleted={() => { setOpenPo(null); load(); }}
        />
      )}
    </div>
  );
}
